import React, {
  FC, FormEvent, SyntheticEvent, useEffect, useState,
} from 'react';
import { FormikValues } from 'formik';
import AutoCompleteInput from '../inputs/autoCompleteInput';
import { ManagedOrganizationInterface } from '../../interfaces/user.interface';

interface OrganizationSelectInterface {
    data?: ManagedOrganizationInterface | null,
    formik?: FormikValues,
    fullWidth?: boolean,
    size?: 'small' | 'medium',
    setSelectedOrganizationId?: (_newSelectedOrganizationId: string) => void,
    setSelectedOrganization?: (_newSelectedOrganization: ManagedOrganizationInterface | null) => void,
    disabled?: boolean,
    label: string,
    placeholder: string,
    options?: ManagedOrganizationInterface[],
    loading?: boolean,
}

const OrganizationSelect: FC<OrganizationSelectInterface> = (props) => {
  const [selectedValue, setSelectedValue] = useState<ManagedOrganizationInterface | null>(props.data || null);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const formOrganizationId = props.formik?.values?.organizationId as string | undefined;

  useEffect(() => {
    if (props.data) {
      setSelectedValue(props.data);
    }
  }, [props.data]);

  useEffect(() => {
    if (!props.data && formOrganizationId && props.options?.length) {
      const matchedOption = props.options.find((option) => option.orgId === formOrganizationId);
      if (matchedOption) {
        setSelectedValue(matchedOption);
      }
    }
  }, [formOrganizationId, props.data, props.options]);

  const onChangeAutoComplete = async (event: FormEvent, newValue: ManagedOrganizationInterface) => {
    if (newValue?.orgId) {
      setSelectedValue(newValue);
      props.formik?.setFieldValue?.('organizationId', newValue.orgId);
      props.formik?.setFieldTouched?.('organizationId', true);
      props.setSelectedOrganizationId?.(String(newValue.orgId));
      props.setSelectedOrganization?.(newValue);
      setSearchedValue('');
    } else {
      setSelectedValue(null);
      props.formik?.setFieldValue?.('organizationId', '');
      props.formik?.setFieldTouched?.('organizationId', true);
      props.setSelectedOrganizationId?.('');
      props.setSelectedOrganization?.(null);
      setSearchedValue('');
    }
  };

  const onInputChangeAutoComplete = (event: SyntheticEvent, value: string) => {
    setSelectedValue(null);
    setSearchedValue(value);
  };

  return (
    <AutoCompleteInput
      key="organizationId"
      name="organizationId"
      label={props.label}
      placeholder={props.placeholder}
      size={props.size}
      style={{ minWidth: 190 }}
      options={props.options || []}
      getOptionLabel={(option: ManagedOrganizationInterface) => option?.orgName || ''}
      value={selectedValue}
      loading={props.loading}
      onChange={(event: FormEvent, newValue: ManagedOrganizationInterface) => onChangeAutoComplete(event, newValue)}
      inputValue={selectedValue?.orgName || searchedValue}
      onInputChange={(event: FormEvent, value: string) => onInputChangeAutoComplete(event, value)}
      isOptionEqualToValue={(option: ManagedOrganizationInterface, value: ManagedOrganizationInterface) => (
        option.orgId === value.orgId
      )}
      fullWidth={props.fullWidth}
      disabled={props.disabled}
      error={Boolean(props.formik?.touched?.organizationId) && Boolean(props.formik?.errors?.organizationId)}
      helperText={Boolean(props.formik?.touched?.organizationId) && props.formik?.errors?.organizationId
        ? String(props.formik?.errors?.organizationId)
        : undefined}
    />
  );
};

export default OrganizationSelect;
